import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Header, Footer, Page } from "@/components/Shell";
import { API } from "@/lib/brand";

const TERMS_TEXT = "J'accepte les Conditions de vente et les Conditions d'utilisation du Révélateur™.";
const MARKETING_TEXT = "Je souhaite recevoir les publications, invitations et communications stratégiques de Maison Beaudette™. Je pourrai retirer mon consentement en tout temps.";

export default function Payment() {
  const { token } = useParams();
  const [config, setConfig] = useState(null);
  const [client, setClient] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [marketingAccepted, setMarketingAccepted] = useState(false);

  useEffect(() => {
    (async () => {
      const [cfg, cli] = await Promise.all([
        axios.get(`${API}/config`),
        axios.get(`${API}/clients/${token}`).catch(() => ({ data: null })),
      ]);
      setConfig(cfg.data);
      setClient(cli.data);
    })();
  }, [token]);

  const saveConsent = async () => {
    await axios.post(`${API}/clients/${token}/consent`, {
      terms_accepted: true,
      marketing_accepted: marketingAccepted,
      terms_text_version: TERMS_TEXT,
      marketing_text_version: MARKETING_TEXT,
    });
  };

  const startCheckout = async () => {
    if (!termsAccepted) return;
    setProcessing(true);
    try {
      await saveConsent();
      const { data } = await axios.post(`${API}/payments/checkout/${token}`);
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error("Session non initialisée.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Impossible d'initier le paiement. Merci de réessayer dans quelques instants.");
      setProcessing(false);
    }
  };

  return (
    <Page tone="ivory">
      <Header variant="ivory" />
      <section className="max-w-3xl mx-auto px-6 md:px-10 pt-20 pb-24" data-testid="payment-page">
        <div className="overline mb-6">Accès à votre lecture</div>
        <h1 className="font-display text-[42px] md:text-[52px] leading-tight text-ink">Recevoir ma lecture stratégique.</h1>
        <p className="mt-6 text-[18px] text-text-2 leading-relaxed max-w-xl">
          Vos réponses sont prêtes. Une fois le paiement confirmé, nous préparons votre Rapport de Révélation Stratégique™.
        </p>

        {/* Résumé de commande */}
        <div className="mt-12 border border-[color:var(--border-bronze)] bg-stone rounded-sm" data-testid="payment-summary">
          <div className="p-8">
            <div className="flex items-baseline justify-between border-b border-[color:var(--border-bronze)] pb-6">
              <div>
                <div className="overline">Résumé de commande</div>
                <div className="font-display text-[22px] text-ink mt-2">Le Révélateur™ — Lecture stratégique</div>
                <div className="text-text-2 text-[14px] mt-1">Rapport de Révélation Stratégique™ complet, PDF et lien privé.</div>
              </div>
              <div className="font-display text-[34px] text-ink" data-testid="price-display">49 $ <span className="text-[13px] text-text-2 uppercase tracking-widest">CAD</span></div>
            </div>

            <dl className="pt-6 grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 text-[14px]" data-testid="order-review">
              <div className="flex justify-between sm:block">
                <dt className="text-text-2 uppercase tracking-[0.2em] text-[11px]">Produit</dt>
                <dd className="text-ink">Le Révélateur™ (produit numérique)</dd>
              </div>
              <div className="flex justify-between sm:block">
                <dt className="text-text-2 uppercase tracking-[0.2em] text-[11px]">Prix</dt>
                <dd className="text-ink">49 $ CAD</dd>
              </div>
              <div className="flex justify-between sm:block">
                <dt className="text-text-2 uppercase tracking-[0.2em] text-[11px]">Taxes</dt>
                <dd className="text-ink">Selon les taxes applicables confirmées à l'étape de paiement</dd>
              </div>
              <div className="flex justify-between sm:block">
                <dt className="text-text-2 uppercase tracking-[0.2em] text-[11px]">Devise</dt>
                <dd className="text-ink">CAD</dd>
              </div>
              <div className="flex justify-between sm:block">
                <dt className="text-text-2 uppercase tracking-[0.2em] text-[11px]">Paiement</dt>
                <dd className="text-ink">Carte bancaire (traitement sécurisé)</dd>
              </div>
              <div className="flex justify-between sm:block">
                <dt className="text-text-2 uppercase tracking-[0.2em] text-[11px]">Accès</dt>
                <dd className="text-ink">Lien privé + courriel</dd>
              </div>
              <div className="flex justify-between sm:block col-span-full">
                <dt className="text-text-2 uppercase tracking-[0.2em] text-[11px]">Remboursement</dt>
                <dd className="text-ink">Voir <Link to="/conditions-de-vente" className="underline text-bronze-dark hover:text-ink" data-testid="link-refund">Conditions de vente</Link></dd>
              </div>
              <div className="flex justify-between sm:block col-span-full">
                <dt className="text-text-2 uppercase tracking-[0.2em] text-[11px]">Soutien</dt>
                <dd className="text-ink">direction@maisonbeaudette.com</dd>
              </div>
            </dl>

            {client && (
              <div className="mt-6 pt-4 border-t border-[color:var(--border-bronze)] text-[13px] text-text-2" data-testid="client-review">
                <div>Achat associé à&nbsp;: <span className="text-ink">{client.company_name}</span> · <span className="text-ink" data-testid="review-email">{client.email}</span></div>
                <div className="mt-1">
                  <Link to="/commencer" className="underline hover:text-ink" data-testid="link-back-edit">Corriger mes renseignements</Link>
                  <span className="mx-3">·</span>
                  <Link to="/" className="underline hover:text-ink" data-testid="link-cancel">Annuler et revenir à l'accueil</Link>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Avis de confidentialité */}
        <div className="mt-6 text-[13px] text-text-2 leading-relaxed" data-testid="privacy-notice">
          Les renseignements fournis sont utilisés pour traiter votre achat, vous donner accès au Révélateur™
          et générer votre rapport. Consultez la <Link to="/politique-de-confidentialite" className="underline text-bronze-dark hover:text-ink" data-testid="link-privacy">Politique de confidentialité</Link>.
        </div>

        {/* Consentement obligatoire */}
        <div className="consent-block mt-6" data-testid="consent-terms">
          <label className="flex gap-3 items-start cursor-pointer">
            <input
              type="checkbox"
              className="consent-checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              data-testid="consent-terms-checkbox"
            />
            <span className="consent-label">
              J'accepte les <Link to="/conditions-de-vente" data-testid="link-terms-sales">Conditions de vente</Link> et
              les <Link to="/conditions-utilisation" data-testid="link-terms-use">Conditions d'utilisation</Link> du Révélateur™.
            </span>
          </label>
        </div>

        {/* Consentement marketing facultatif */}
        <div className="consent-block mt-3" data-testid="consent-marketing">
          <label className="flex gap-3 items-start cursor-pointer">
            <input
              type="checkbox"
              className="consent-checkbox"
              checked={marketingAccepted}
              onChange={(e) => setMarketingAccepted(e.target.checked)}
              data-testid="consent-marketing-checkbox"
            />
            <span className="consent-label">{MARKETING_TEXT}</span>
          </label>
          <div className="text-[12px] text-text-2/80 mt-2 pl-8">Le refus n'empêche ni le paiement, ni l'accès, ni le rapport, ni les courriels transactionnels.</div>
        </div>

        {/* CTA — redirection Stripe Checkout */}
        <div className="mt-8" data-testid="payment-zone">
          {!config ? (
            <div className="text-text-2 text-sm">Chargement…</div>
          ) : (
            <>
              <button
                onClick={startCheckout}
                disabled={!termsAccepted || processing || !config.stripe_enabled}
                className="btn-primary w-full"
                data-testid="btn-start-checkout"
              >
                {processing ? "Redirection sécurisée en cours…" : "Procéder au paiement"}
              </button>
              {!termsAccepted && (
                <div className="mt-3 text-[13px] text-text-2/80 text-center" data-testid="payment-blocked">
                  Cochez la case ci-dessus pour activer le paiement.
                </div>
              )}
              {!config.stripe_enabled && (
                <div className="mt-3 text-[13px] text-bronze-dark text-center" data-testid="payment-disabled">
                  Le paiement est temporairement indisponible. Merci de réessayer sous peu.
                </div>
              )}
            </>
          )}
        </div>

        <p className="mt-8 text-[12px] tracking-[0.26em] uppercase text-text-2">Paiement sécurisé · Carte bancaire · Confidentialité assurée</p>
      </section>
      <Footer />
    </Page>
  );
}
